import { atCostUnitCostOf, repriceAtCostUses } from "@/lib/at-cost-pricing";
import createId from "@/lib/create-id";
import {
  type PriceBucket,
  applyDiscountToBuckets,
  mergeSamePriceBuckets,
  topUpsFirst,
} from "@/lib/discount-price-buckets";
import {
  type DiscountUseData,
  calculateDiscountAmount,
} from "@/lib/discount-utils";
import { formatCurrency, formatToHumanReadable } from "@/lib/format-data";

import {
  CreateCustomDiscountRequest,
  CreditPoolTiny,
  DiscountDetail,
  DiscountUse,
} from "@/types/billing/discount";
import { ChargeItem } from "@/types/charge";
import { DiscountType } from "@/types/enums/discount";
import { ItemType } from "@/types/enums/item-type";
import { Item } from "@/types/inventory/item";

/**
 * Item types that cannot have discounts applied. Gift cards are intentionally
 * NOT here — a discount lowers what the patient pays, not the card's value.
 */
export const NON_DISCOUNTABLE_ITEM_TYPES: ItemType[] = [
  ItemType.PACKAGE,
  ItemType.MEMBERSHIP,
];

/**
 * Whether an item falls under a discount's `itemType` target.
 *
 * PRODUCT, SERVICE, MEDICATION and GIFT_CARD each match their own type;
 * every other target matches nothing.
 */
export function matchesItemTypeTarget(
  target: ItemType,
  itemType: ItemType | undefined,
): boolean {
  const isService = itemType === ItemType.SERVICE;
  const isProduct = itemType === ItemType.PRODUCT;
  const isMedication = itemType === ItemType.MEDICATION;
  return (
    (target === ItemType.GIFT_CARD && itemType === ItemType.GIFT_CARD) ||
    (target === ItemType.SERVICE && isService) ||
    (target === ItemType.PRODUCT && isProduct) ||
    (target === ItemType.MEDICATION && isMedication)
  );
}

/**
 * Centralized discount management utilities for checkout
 * Handles creating, loading, and using discounts
 */

// Re-export calculateDiscountAmount for backwards compatibility
export { calculateDiscountAmount } from "@/lib/discount-utils";

/**
 * Normalize a list of discounts to ensure order and quantity are set.
 * This should be called whenever discounts are modified.
 * Note: order is always set based on array index to ensure consistency after reordering.
 *
 * When itemPrice is provided, discount amounts are recalculated to reflect
 * sequential/compounding application. This ensures that a simple sum of
 * all DiscountUse.amount values equals the correct compounding total.
 */
export function normalizeDiscounts(
  discounts: DiscountUse[],
  itemQuantity: number,
  itemPrice?: number,
): DiscountUse[] {
  // An at-cost promotion use prices the line down to cost after every other
  // discount, so it goes last and is recomputed whenever the line changes —
  // a benefit added after it can't take the line below cost.
  const hasAtCost = discounts.some((d) => atCostUnitCostOf(d) != null);
  const ordered = hasAtCost
    ? [
        ...discounts.filter((d) => atCostUnitCostOf(d) == null),
        ...discounts.filter((d) => atCostUnitCostOf(d) != null),
      ]
    : discounts;
  const normalized = ordered.map((discount, index) => ({
    ...discount,
    // Stamp an id the first time a use is seen and never reassign it. A charge is
    // persisted on its first tender, so on a split payment the rest of the cart is
    // re-sent to a charge that already exists; the id is how the backend tells a
    // discount it has already written from one the cashier added afterwards.
    id: discount.id ?? createId("duc"),
    order: index, // Always use index to ensure order reflects actual array position
    quantity: discount.quantity ?? itemQuantity,
  }));

  // Recalculate amounts for sequential application when item price is available
  if (itemPrice !== undefined && itemPrice > 0 && itemQuantity > 0) {
    const sequential = recalculateSequentialAmounts(
      normalized,
      itemPrice,
      itemQuantity,
    );
    return hasAtCost
      ? repriceAtCostUses(sequential, itemPrice, itemQuantity)
      : sequential;
  }

  // A $0-priced line never reaches the recalc above, so it owes the same
  // int-quantity contract here: backend DiscountUse.quantity is an int, and
  // fractional coverage rounds up to whole units (0.5 of a syringe reads as 1).
  // Left unrounded it is rejected at the API boundary rather than coerced.
  //
  // Deliberately keyed on a zero price, not on the absent-price fallthrough:
  // recalculateDiscountsForItem normalizes without a price and then again with
  // one, and a quantity ceiled on that first pass becomes the divisor for the
  // per-unit rate on the second, shrinking fixed-amount discounts.
  if (itemPrice === 0) {
    return normalized.map((discount) => ({
      ...discount,
      quantity: Math.ceil(discount.quantity),
      // Nothing to take off a free line, at cost or otherwise.
      ...(atCostUnitCostOf(discount) != null ? { amount: 0 } : {}),
    }));
  }

  return normalized;
}

/**
 * Recalculate discount amounts to reflect sequential/compounding application.
 * Each discount's amount is updated to reflect what it actually contributes
 * when applied in order to the running total, rather than independently.
 * This ensures that sum(amounts) equals the correct sequential total.
 */
function recalculateSequentialAmounts(
  discounts: DiscountUse[],
  itemPrice: number,
  itemQuantity: number,
): DiscountUse[] {
  if (discounts.length === 0) {
    return discounts;
  }

  const discountData = convertDiscountUsesToData(discounts);
  // Track like-priced units as buckets rather than an index-per-unit array —
  // Array(itemQuantity) throws RangeError for a fractional line quantity like
  // 1.5, and inflating to Math.ceil() overpays per-unit amount discounts.
  // Buckets carry the exact fractional count through the allocation.
  let buckets: PriceBucket[] = [
    { quantity: itemQuantity, remainingPrice: itemPrice },
  ];
  const maxDiscountTotal = itemPrice * itemQuantity;
  let roundedDiscountTotal = 0;

  const applyOne = (discountUse: DiscountUse, idx: number): DiscountUse => {
    const data = discountData[idx];

    // Preserve exact proportional share for fixed global discounts — recalculating
    // from the rounded per-unit createDiscount.amount can drift by 1 cent. Global
    // percentage discounts must still be recalculated on the running discounted
    // price so they stack sequentially instead of adding percentages together.
    const isGlobal = isGlobalDiscountUse(discountUse);
    const isGlobalPercentage =
      isGlobal && data.percentage !== undefined && data.percentage > 0;
    if (isGlobal && !isGlobalPercentage) {
      const cappedQuantity = Math.min(discountUse.quantity, itemQuantity);
      // Still deduct from bucket prices so later discounts see correct remaining.
      // Spread by what each unit has left, so a unit an earlier discount already
      // emptied doesn't swallow part of the amount.
      const remainingTotal = buckets.reduce(
        (sum, bucket) => sum + bucket.remainingPrice * bucket.quantity,
        0,
      );
      const share =
        remainingTotal > 0
          ? Math.min(1, discountUse.amount / remainingTotal)
          : 0;
      buckets = mergeSamePriceBuckets(
        buckets.map((bucket) => ({
          ...bucket,
          remainingPrice: bucket.remainingPrice * (1 - share),
        })),
      );
      roundedDiscountTotal = Math.min(
        maxDiscountTotal,
        roundedDiscountTotal + discountUse.amount,
      );
      return {
        ...discountUse,
        // Ceil to a whole unit — backend DiscountUse.quantity is int, and
        // its cash-replay logic expects fractional whole-line coverage to
        // round up (a 1.5-unit line covered by one discount reads as 2).
        quantity: Math.ceil(cappedQuantity),
      };
    }

    // Apply to units with the highest remaining value first (maximizes discount).
    const applied = applyDiscountToBuckets(
      buckets,
      data,
      itemPrice,
      itemQuantity,
    );
    buckets = applied.buckets;
    const totalDiscountForThis = applied.discount;
    const appliedQuantityTotal = applied.appliedQuantity;
    const roundedAmount = Math.min(
      Math.round(totalDiscountForThis),
      Math.max(0, Math.round(maxDiscountTotal) - roundedDiscountTotal),
    );
    roundedDiscountTotal += roundedAmount;

    return {
      ...discountUse,
      amount: roundedAmount,
      // See the global-flat branch above — DiscountUse.quantity is int
      // on the backend; ceil the fractional appliedQuantityTotal so a
      // 1.5-unit line reads as covering 2 (matches cash-replay).
      quantity: Math.ceil(appliedQuantityTotal),
    };
  };

  // Uses keep their positions on the line; only the order they're worked out changes.
  const results: DiscountUse[] = [...discounts];
  for (const idx of topUpsFirst(discountData)) {
    results[idx] = applyOne(discounts[idx], idx);
  }
  return results;
}

/**
 * Create a DiscountUse object from an existing DiscountDetail
 * Used when applying a loaded discount to a charge item
 */
export function createDiscountUseFromDiscount(
  discount: DiscountDetail,
  itemPrice: number,
  quantity: number,
): DiscountUse {
  return {
    discountId: discount.id,
    amount: calculateDiscountAmount(discount, itemPrice, quantity),
    quantity,
    order: 0, // Will be normalized when added to list
    discount,
  };
}

/**
 * Create a DiscountUse object for an custom discount
 * Used when creating a new discount during checkout
 */
export function createDiscountUseFromCustom(
  createDiscountData: CreateCustomDiscountRequest,
  itemPrice: number,
  quantity: number,
): DiscountUse {
  const amount = calculateDiscountAmount(
    createDiscountData,
    itemPrice,
    quantity,
  );
  return {
    discountId: undefined, // Will be set when discount is created on backend
    amount,
    quantity,
    order: 0, // Will be normalized when added to list
    createDiscount: createDiscountData,
  };
}

/**
 * Create a DiscountUse object for an custom discount from item details
 * Convenience function that determines itemType automatically
 */
export function createCustomDiscountUse(
  patientId: string,
  item: Item,
  itemPrice: number,
  quantity: number,
  amount?: number,
  percentage?: number,
  reason?: string,
  comment?: string,
  discountReasonId?: string | null,
): DiscountUse {
  const createDiscountData: CreateCustomDiscountRequest = {
    patientId,
    itemId: item.id,
    category: item.category || undefined,
    itemType: item.type,
    amount,
    percentage,
    quantity: 1, // Single use discount
    reason,
    comment,
    discountReasonId,
  };

  return createDiscountUseFromCustom(createDiscountData, itemPrice, quantity);
}

/**
 * Convert DiscountUse array to DiscountUseData array for sequential calculation
 * Extracts the discount definition (percentage or fixed amount) for proper compounding
 */
function convertDiscountUsesToData(
  discounts: DiscountUse[],
): DiscountUseData[] {
  return discounts.map((discountUse) => {
    const quantity = discountUse.quantity ?? 1;

    const additionalCharge =
      discountUse.discount?.additionalCharge ??
      discountUse.createDiscount?.additionalCharge;
    const coversAll =
      (discountUse.discount?.percentage ??
        discountUse.createDiscount?.percentage) === 100;
    if (additionalCharge && coversAll) {
      return { additionalCharge, quantity };
    }

    // Prefer amount over percentage when both exist - amount is more accurate
    const fixedAmount =
      discountUse.discount?.amount ?? discountUse.createDiscount?.amount;

    if (fixedAmount && fixedAmount > 0) {
      // Derive per-unit from discountUse.amount (the source-of-truth total)
      // to avoid rounding drift when createDiscount.amount stores
      // Math.round(total / quantity) for amounts not evenly divisible.
      const perUnit =
        discountUse.amount > 0 && quantity > 0
          ? discountUse.amount / quantity
          : fixedAmount;
      return { amount: perUnit, quantity };
    }

    // For percentage discounts
    const percentage =
      discountUse.discount?.percentage ??
      discountUse.createDiscount?.percentage;

    if (percentage && percentage > 0) {
      return {
        percentage,
        quantity,
      };
    }

    // Fallback: reverse-calculate from the total amount
    // This handles edge cases where discount details aren't available
    const perItemAmount = quantity > 0 ? discountUse.amount / quantity : 0;
    return {
      amount: perItemAmount,
      quantity,
    };
  });
}

/**
 * Get the total discount amount from an array of DiscountUse objects.
 * Discount amounts are stored as sequential/compounding values by normalizeDiscounts,
 * so a simple sum gives the correct total.
 */
export function getTotalDiscountAmount(
  discounts: DiscountUse[] | undefined,
): number {
  if (!discounts || discounts.length === 0) {
    return 0;
  }

  return discounts.reduce((sum, discountUse) => sum + discountUse.amount, 0);
}

/**
 * Check if a discount is applicable to an item.
 *
 * `categoryAncestorsByName` (optional) lets the caller honor nested categories:
 * a discount keyed on "Injectables" should also apply to items in "Botox" /
 * "Dysport" / etc. Callers that already build this map (e.g. checkout) pass it
 * in; legacy call sites without it fall back to exact match (still correct for
 * flat-tagged items).
 */
export function isDiscountApplicable(
  discount: DiscountDetail,
  item: Pick<Item, "id" | "category" | "type">,
  categoryAncestorsByName?: Map<string, string[]>,
): boolean {
  // Skip archived discounts
  if (discount.isArchived) {
    return false;
  }

  // Skip expired discounts
  if (discount.expiryDate) {
    const expiryDate = new Date(discount.expiryDate);
    if (expiryDate < new Date()) {
      return false;
    }
  }

  // A discount has at most one target. Item-specific discounts apply only to
  // their exact item, so short-circuit rather than widening the match.
  if (discount.itemId) {
    return discount.itemId === item.id;
  }

  // Check if discount applies to the item's category — exact match, OR a discount
  // keyed on any ancestor category when the caller supplied the closure map. The
  // backend `discount_eligibility.py` does the same expansion via
  // `category_ancestors_subquery`; we mirror it here so the UI and server agree
  // on which discounts apply to a child-category item.
  if (discount.category) {
    const categoryMatches =
      item.category === discount.category ||
      (item.category
        ? (categoryAncestorsByName
            ?.get(item.category)
            ?.includes(discount.category) ?? false)
        : false);
    if (!categoryMatches) {
      return false;
    }
  }

  // Check if discount applies to product/service type.
  if (discount.itemType) {
    if (!matchesItemTypeTarget(discount.itemType, item.type)) {
      return false;
    }
  }

  if (discount.category || discount.itemType) {
    return true;
  }

  // Check for global discounts (no specific item/category/itemType restrictions)
  if (
    discount.type === DiscountType.GLOBAL ||
    (!discount.itemId && !discount.category && !discount.itemType)
  ) {
    // Prevent global discounts from applying to non-discountable item types
    if (NON_DISCOUNTABLE_ITEM_TYPES.includes(item.type)) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * How good a discount is for an item, as a percentage of its price. A prepaid use
 * with an extra charge ranks by what it saves, so a free benefit beats a top-up.
 */
function discountRankValue(
  discount: DiscountDetail,
  itemPrice?: number,
): number {
  const basePrice = itemPrice && itemPrice > 0 ? itemPrice : 10000;
  if (discount.additionalCharge && discount.percentage === 100) {
    return (calculateDiscountAmount(discount, basePrice, 1) / basePrice) * 100;
  }
  if (discount.percentage && discount.percentage > 0) {
    return discount.percentage;
  }
  if (discount.amount && discount.amount > 0) {
    return (discount.amount / basePrice) * 100;
  }
  return 0;
}

/**
 * Find the best available discount for an item from a list of discounts
 * Returns the discount with the highest value (amount or percentage)
 */
export function findBestDiscount(
  item: Item,
  availableDiscounts: DiscountDetail[],
  itemPrice?: number,
  categoryAncestorsByName?: Map<string, string[]>,
): DiscountDetail | null {
  if (!availableDiscounts || availableDiscounts.length === 0) {
    return null;
  }

  // Re-filters rather than trusting the caller, so the ancestor map has to come
  // along too — otherwise a parent-category benefit that `getApplicableDiscounts`
  // just admitted gets dropped again right here.
  const applicableDiscounts = availableDiscounts.filter((discount) =>
    isDiscountApplicable(discount, item, categoryAncestorsByName),
  );

  if (applicableDiscounts.length === 0) {
    return null;
  }

  // Find the best discount (highest discount amount or percentage)
  let bestDiscount = applicableDiscounts[0];
  let bestDiscountValue = 0;

  for (const discount of applicableDiscounts) {
    const discountValue = discountRankValue(discount, itemPrice);

    // Skip degenerate 0-value discounts
    if (discountValue <= 0) continue;

    if (discountValue > bestDiscountValue) {
      bestDiscountValue = discountValue;
      bestDiscount = discount;
    }
  }

  // If no discount had a positive value, return null
  if (bestDiscountValue <= 0) {
    return null;
  }

  return bestDiscount;
}

/**
 * Get all applicable discounts for an item (not just the best one)
 */
export function getApplicableDiscounts(
  item: Item,
  availableDiscounts: DiscountDetail[],
  categoryAncestorsByName?: Map<string, string[]>,
): DiscountDetail[] {
  if (!availableDiscounts || availableDiscounts.length === 0) {
    return [];
  }

  return availableDiscounts.filter((discount) =>
    isDiscountApplicable(discount, item, categoryAncestorsByName),
  );
}

/** The discount kinds a plan authors, and so the only ones with a stacking intent. */
const BENEFIT_DISCOUNT_TYPES: DiscountType[] = [
  DiscountType.MEMBERSHIP,
  DiscountType.PACKAGE,
];

/**
 * Whether a plan authored this discount, and so whether its stackability was
 * ever a deliberate choice.
 *
 * `Discount.is_stackable` defaults to false on every row, and checkout
 * persists a cashier's custom discount and an order-level GLOBAL allocation as
 * ordinary rows carrying that default. Reopen the charge and they come back
 * indistinguishable from a benefit a plan marked non-combinable — so the
 * stacking rules have to ask what kind of discount this is before believing
 * the flag.
 */
export function isPlanBenefit(discount: DiscountDetail): boolean {
  return BENEFIT_DISCOUNT_TYPES.includes(discount.type);
}

/** A plan benefit whose issuing template forbids combining it with any other. */
export function isNonStackable(discount: DiscountDetail): boolean {
  return discount.isStackable === false && isPlanBenefit(discount);
}

/**
 * Narrow `candidates` to the benefits allowed to govern one line together.
 *
 * A benefit issued as non-stackable has to be the only benefit on its line.
 * Deciding that up front is what keeps a line honest: left to fill units
 * greedily, checkout picks the highest-*rate* benefit first, and when that
 * benefit can only cover part of the line it tops the rest up with whatever
 * else applies — which is how a membership's fixed "$2 off a unit of Botox"
 * ends up sitting beside the same plan's blanket "15% off all products".
 *
 * So the two options are priced against each other for the whole line: every
 * stackable benefit filling it together, versus the single best non-stackable
 * benefit on its own. Greatest savings wins and ties go to the non-stackable
 * offer, mirroring the rule ShopService already applies to public checkout
 * (`_normalize_shop_line_promotion_discounts`).
 *
 * This decides an *untouched* line. A line that already carries a benefit is
 * settled by that benefit instead (see `reserveBestForNewUnits`), because
 * reservation never re-derives line state a cashier may have edited — so a
 * capped benefit already applied stays put and the units beyond its cap ring
 * up at full price rather than pulling in a second benefit. Swapping to the
 * larger benefit is then a cashier action in the line's discount panel, where
 * applying one replaces the other.
 */
export function selectStackablePool({
  candidates,
  unitPrice,
  lineQuantity,
  cartContext,
}: {
  /** Pre-filtered to the line's item: everything here is priced at `unitPrice`. */
  candidates: DiscountDetail[];
  unitPrice: number;
  lineQuantity: number;
  /** Lines whose existing uses have already spent finite benefit capacity. */
  cartContext: ChargeItem[];
}): DiscountDetail[] {
  const nonStackable = candidates.filter(isNonStackable);
  if (nonStackable.length === 0) return candidates;
  const stackable = candidates.filter((d) => !isNonStackable(d));

  const coverable = (discount: DiscountDetail, units: number): number =>
    Math.max(
      0,
      Math.min(units, getRemainingQuantityForDiscount(discount, cartContext)),
    );
  /** What a benefit is worth if it governed this line by itself. */
  const soloWorth = (discount: DiscountDetail): number => {
    const covered = coverable(discount, lineQuantity);
    return covered > 0
      ? calculateDiscountAmount(discount, unitPrice, covered)
      : 0;
  };

  // Stackable benefits share the line, so their combined worth is bounded by
  // the units they can cover between them — the same best-first fill the
  // reservation loop performs, priced without mutating anything.
  let unitsLeft = lineQuantity;
  const stackableWorth = [...stackable]
    .sort((a, b) => soloWorth(b) - soloWorth(a))
    .reduce((total, discount) => {
      const covered = coverable(discount, unitsLeft);
      if (covered <= 0) return total;
      unitsLeft -= covered;
      return total + calculateDiscountAmount(discount, unitPrice, covered);
    }, 0);

  const bestNonStackable = nonStackable.reduce((best, discount) =>
    soloWorth(discount) > soloWorth(best) ? discount : best,
  );
  return soloWorth(bestNonStackable) >= stackableWorth
    ? [bestNonStackable]
    : stackable;
}

/**
 * The uses already on a line that may stay when `incoming` is applied to it.
 *
 * Non-stackability is symmetric: whether the benefit arriving or the one
 * already there is what refuses to combine, they cannot both govern the line,
 * so the arriving benefit replaces it. Uses with no issued benefit behind them
 * — a cashier's custom discount, an order-level allocation — are a separate
 * deliberate act and always stay.
 *
 * Every hand-apply surface routes through here, so the register answers the
 * question the same way wherever a cashier applies a benefit from.
 */
export function retainableWith(
  incoming: DiscountDetail,
  existing: DiscountUse[],
  availableDiscounts: DiscountDetail[] = [],
): DiscountUse[] {
  return existing.filter((use) => {
    if (!use.discountId || use.discountId === incoming.id) return true;
    const applied =
      use.discount ?? availableDiscounts.find((d) => d.id === use.discountId);
    if (!applied) return true;
    // Only a plan benefit can be displaced. The collision is symmetric between
    // two benefits, but a cashier's own discount is never collateral.
    if (!isPlanBenefit(applied)) return true;
    return !isNonStackable(incoming) && !isNonStackable(applied);
  });
}

/**
 * Calculate how many units a DiscountUse covers.
 * Prefers the explicitly stored quantity (always set by normalizeDiscounts).
 * Falls back to reverse-calculation for legacy data without quantity field.
 */
export function getQuantityFromDiscountUse(
  discountUse: DiscountUse,
  discount: DiscountDetail,
  itemPrice: number,
): number {
  if (!discountUse.discountId || discountUse.discountId !== discount.id) {
    return 0;
  }

  // Prefer the explicitly stored quantity (always set by normalizeDiscounts)
  if (discountUse.quantity != null && discountUse.quantity > 0) {
    return discountUse.quantity;
  }

  // Fallback: reverse-calculate from amount (for legacy data without quantity)
  if (discount.amount && discount.amount > 0) {
    return Math.floor(discountUse.amount / discount.amount);
  } else if (discount.percentage && discount.percentage > 0) {
    if (itemPrice > 0) {
      return Math.floor(
        (discountUse.amount * 100) / (itemPrice * discount.percentage),
      );
    }
  }

  return 0;
}

/**
 * Calculate total quantity used for a discount from all DiscountUse objects in charge items
 * This counts how many units have been discounted across all charge items
 * Note: An item can have multiple DiscountUse objects for the same discount (e.g., when adding more units)
 */
export function getUsedQuantityFromChargeItems(
  discount: DiscountDetail,
  chargeItems: ChargeItem[],
): number {
  let totalUsed = 0;

  chargeItems.forEach((item) => {
    // Find ALL discount uses for this discount (not just the first one)
    const discountUses =
      item.discounts?.filter((d) => d.discountId === discount.id) || [];

    discountUses.forEach((discountUse) => {
      const quantity = getQuantityFromDiscountUse(
        discountUse,
        discount,
        item.price,
      );
      totalUsed += quantity;
    });
  });

  return totalUsed;
}

/**
 * Calculate remaining quantity available for a discount after accounting for current charge
 */
export function getRemainingQuantityForDiscount(
  discount: DiscountDetail,
  chargeItems: ChargeItem[],
): number {
  // Infinite discounts (quantity is null) always have remaining quantity
  if (discount.quantity == null) {
    return Number.MAX_SAFE_INTEGER; // Return a very large number for infinite
  }
  const usedQuantity = getUsedQuantityFromChargeItems(discount, chargeItems);
  const remaining =
    (discount.quantityRemaining ?? discount.quantity) - usedQuantity;
  if (discount.creditPool && discount.creditCost) {
    // A credits option also shares one balance with the rest of its group.
    const affordable = Math.floor(
      getCreditsLeftInCart(discount.creditPool, chargeItems) /
        discount.creditCost,
    );
    return Math.max(0, Math.min(remaining, affordable));
  }
  return Math.max(0, remaining);
}

/**
 * The pick-one group a benefit belongs to. Credits options share a group id too,
 * but they are spent from one balance in any mix, never picked one of.
 */
export function pickOneChoiceId(discount: {
  discountChoiceId?: string;
  creditPool?: CreditPoolTiny | null;
}): string | undefined {
  return discount.creditPool ? undefined : discount.discountChoiceId;
}

/**
 * Credits a credits group's pool has left once every use of it already in the
 * cart is paid for.
 */
export function getCreditsLeftInCart(
  pool: CreditPoolTiny,
  chargeItems: ChargeItem[],
): number {
  let spent = 0;
  chargeItems.forEach((item) => {
    item.discounts?.forEach((use) => {
      const definition = use.discount;
      if (definition?.creditPool?.id !== pool.id || !definition.creditCost)
        return;
      spent +=
        getQuantityFromDiscountUse(use, definition, item.price) *
        definition.creditCost;
    });
  });
  return Math.max(0, pool.creditsRemaining - spent);
}

/**
 * Returns true if two charge items represent the same line (for excluding the current item
 * when counting discount usage in other items). Uses item identity (id for custom amounts,
 * itemId + soldPackageId for regular/package items) rather than object reference, since
 * we may be recalculating for a clone (e.g. tempUpdatedItem) that isn't in allChargeItems.
 * Gift cards are intentionally duplicate-able, so they only match by row id/reference.
 */
function isGiftCardChargeItemLine(item: ChargeItem): boolean {
  return item.item?.type === ItemType.GIFT_CARD;
}

function isSameChargeItemLine(a: ChargeItem, b: ChargeItem): boolean {
  if (a === b) return true;
  if (a.id && b.id) return a.id === b.id;

  if (isGiftCardChargeItemLine(a) || isGiftCardChargeItemLine(b)) {
    return false;
  }

  if (a.itemId && b.itemId && a.itemId === b.itemId) {
    // Same item sold by different providers is kept on separate lines by
    // addItemToCharge, so treat them as distinct here too — otherwise the
    // cross-item discount accounting excludes a sibling row and the same
    // credit gets claimed on both.
    return (
      a.soldPackageId === b.soldPackageId &&
      a.soldByProviderId === b.soldByProviderId
    );
  }
  return false;
}

function isGlobalDiscountUse(discountUse: DiscountUse): boolean {
  return (
    discountUse.createDiscount?.type === DiscountType.GLOBAL ||
    discountUse.discount?.type === DiscountType.GLOBAL
  );
}

function isGlobalPercentageDiscountUse(discountUse: DiscountUse): boolean {
  const percentage =
    discountUse.discount?.percentage ?? discountUse.createDiscount?.percentage;
  const amount =
    discountUse.discount?.amount ?? discountUse.createDiscount?.amount;

  return (
    isGlobalDiscountUse(discountUse) &&
    percentage !== undefined &&
    percentage > 0 &&
    !(amount !== undefined && amount > 0)
  );
}

/**
 * Recalculate discounts for a charge item when quantity or price changes
 * This ensures discounts are only applied up to available quantity and amounts are correct
 * Consolidates multiple DiscountUse objects for the same discount into a single DiscountUse
 * Also applies additional discounts to units that don't have discounts yet
 * Automatically normalizes order and quantity at the end
 */
export function recalculateDiscountsForItem(
  chargeItem: ChargeItem,
  availableDiscounts: DiscountDetail[],
  allChargeItems: ChargeItem[],
  /**
   * Discount IDs the user has explicitly removed from this checkout. Existing
   * uses on items keep their slot, but the "auto-apply to uncovered units"
   * pass below skips these — removing a multi-use discount from one line must
   * preserve siblings while still preventing a new line from picking it up.
   */
  dismissedDiscountIds?: ReadonlySet<string>,
): DiscountUse[] {
  const recalculatedDiscounts: DiscountUse[] = [];
  const processedDiscountIds = new Set<string>();
  let totalUnitsCovered = 0;

  // Normalize and sort existing discounts by order
  const normalizedDiscounts = normalizeDiscounts(
    chargeItem.discounts || [],
    chargeItem.quantity,
  );
  const sortedDiscounts = [...normalizedDiscounts].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  if (sortedDiscounts.length > 0) {
    sortedDiscounts.forEach((discountUse, index) => {
      if (!discountUse.discountId && discountUse.createDiscount) {
        if (discountUse.createDiscount.type === DiscountType.GLOBAL) {
          // Preserve exact proportional share set by distributeGlobalDiscount.
          // Recalculating here from the rounded per-unit amount would inflate
          // the total by a few cents across items.
          recalculatedDiscounts.push(discountUse);
          return;
        }
        // A same-cart top-up reprices from the line, so the member still owes
        // it, on only the units its credits covered.
        if (discountUse.createDiscount.additionalCharge) {
          const units = Math.min(
            discountUse.quantity || chargeItem.quantity,
            chargeItem.quantity,
          );
          const amount = calculateDiscountAmount(
            discountUse.createDiscount,
            chargeItem.price,
            units,
          );
          if (amount > 0) {
            recalculatedDiscounts.push({
              ...discountUse,
              amount,
              quantity: units,
              order: discountUse.order ?? index,
            });
          }
          return;
        }
        // Scale from discountUse.amount (source-of-truth total) to avoid
        // rounding drift when createDiscount.amount = Math.round(total / qty).
        // Cap at item total so free items don't get a positive discount.
        const maxAmount = chargeItem.price * chargeItem.quantity;
        const recalculatedAmount =
          discountUse.amount > 0 && discountUse.quantity > 0
            ? Math.min(
                Math.round(
                  (discountUse.amount / discountUse.quantity) *
                    chargeItem.quantity,
                ),
                maxAmount,
              )
            : calculateDiscountAmount(
                discountUse.createDiscount,
                chargeItem.price,
                chargeItem.quantity,
              );
        recalculatedDiscounts.push({
          ...discountUse,
          amount: recalculatedAmount,
          quantity: chargeItem.quantity,
          order: discountUse.order ?? index,
        });
        // Custom discounts stack with patient discounts, so don't
        // count them toward totalUnitsCovered
      }
    });
  }

  // Collect existing discounts with their details for sorting
  const existingDiscountsWithDetails: Array<{
    discountUse: DiscountUse;
    discount: DiscountDetail;
    value: number;
    originalOrder: number;
  }> = [];

  if (sortedDiscounts.length > 0) {
    for (const discountUse of sortedDiscounts) {
      if (!discountUse.discountId) {
        // Already handled custom discounts above
        continue;
      }

      // Skip if we've already processed this discount
      if (processedDiscountIds.has(discountUse.discountId)) {
        continue;
      }

      // Find the discount detail
      const discount = availableDiscounts.find(
        (d) => d.id === discountUse.discountId,
      );

      if (!discount) {
        // Discount no longer available, skip it
        continue;
      }

      existingDiscountsWithDetails.push({
        discountUse,
        discount,
        value: discountRankValue(discount, chargeItem.price),
        originalOrder: discountUse.order ?? 999, // Preserve original order
      });
    }
  }

  // Sort existing discounts by original order first, then by value (best first)
  existingDiscountsWithDetails.sort((a, b) => {
    if (a.originalOrder !== b.originalOrder) {
      return a.originalOrder - b.originalOrder;
    }
    return b.value - a.value;
  });

  // Process each discount in order, applying them to non-overlapping units
  for (const { discountUse, discount } of existingDiscountsWithDetails) {
    processedDiscountIds.add(discount.id);

    // Calculate how much of this discount is used across ALL items (excluding current item)
    // Use line identity (id or itemId+soldPackageId) since chargeItem may be a clone not in the array
    const otherItems = allChargeItems.filter(
      (item) => !isSameChargeItemLine(item, chargeItem),
    );
    const usedInOtherItems = getUsedQuantityFromChargeItems(
      discount,
      otherItems,
    );

    // Calculate remaining quantity available
    let remainingQuantity: number;
    if (discount.quantity == null) {
      remainingQuantity = Number.MAX_SAFE_INTEGER;
    } else {
      remainingQuantity = Math.max(
        0,
        (discount.quantityRemaining ?? discount.quantity) - usedInOtherItems,
      );
    }

    if (remainingQuantity <= 0) {
      // No remaining quantity, skip this discount
      continue;
    }

    // Calculate how many uncovered units this discount can apply to
    const uncoveredUnits = chargeItem.quantity - totalUnitsCovered;
    const applicableQuantity = Math.min(uncoveredUnits, remainingQuantity);

    if (applicableQuantity <= 0) {
      // Can't apply to any units
      continue;
    }

    // Recalculate the discount amount for the applicable quantity
    const recalculatedAmount = calculateDiscountAmount(
      discount,
      chargeItem.price,
      applicableQuantity,
    );
    // The price dropped to the extra charge or below, so the use saves nothing
    // and must not spend the member's unit or credits.
    if (discount.additionalCharge && recalculatedAmount <= 0) {
      continue;
    }

    // Create consolidated discount use
    const newDiscountUse = {
      ...discountUse,
      amount: recalculatedAmount,
      quantity: applicableQuantity,
      order: discountUse.order ?? recalculatedDiscounts.length,
      discount,
    };
    recalculatedDiscounts.push(newDiscountUse);
    totalUnitsCovered += applicableQuantity;

    // If all units are covered, we're done
    if (totalUnitsCovered >= chargeItem.quantity) {
      break;
    }
  }

  // If we have an item, check for additional discounts for uncovered units
  if (chargeItem.item && availableDiscounts.length > 0) {
    const remainingUnits = chargeItem.quantity - totalUnitsCovered;

    if (remainingUnits > 0) {
      // Find all applicable discounts for this item, sorted by best first.
      // Skip dismissed discounts here only — existing uses on this item were
      // preserved above, but the user has signaled they don't want this
      // discount auto-attached to a line that doesn't already have it.
      const candidateDiscounts =
        dismissedDiscountIds && dismissedDiscountIds.size > 0
          ? availableDiscounts.filter((d) => !dismissedDiscountIds.has(d.id))
          : availableDiscounts;
      const applicableDiscounts = getApplicableDiscounts(
        chargeItem.item,
        candidateDiscounts,
      );

      // Sort by discount value (best first)
      const sortedDiscounts = [...applicableDiscounts].sort(
        (a, b) =>
          discountRankValue(b, chargeItem.price) -
          discountRankValue(a, chargeItem.price),
      );

      // Try to apply additional discounts to uncovered units
      let unitsStillUncovered = remainingUnits;

      for (const discount of sortedDiscounts) {
        // Skip if we've already processed this discount
        if (processedDiscountIds.has(discount.id)) {
          continue;
        }

        // Calculate how much of this discount is used across ALL items (excluding current item)
        // Use line identity since chargeItem may be a clone not in the array
        const otherItems = allChargeItems.filter(
          (item) => !isSameChargeItemLine(item, chargeItem),
        );
        const usedInOtherItems = getUsedQuantityFromChargeItems(
          discount,
          otherItems,
        );

        // Also account for discounts we've already applied in this recalculation
        // (for the current item) to avoid double-counting
        const usedInCurrentItem = recalculatedDiscounts
          .filter((du) => du.discountId === discount.id)
          .reduce((sum, du) => {
            const qty = getQuantityFromDiscountUse(
              du,
              discount,
              chargeItem.price,
            );
            return sum + qty;
          }, 0);

        // Calculate remaining quantity available
        let remainingQuantity: number;
        if (discount.quantity == null) {
          remainingQuantity = Number.MAX_SAFE_INTEGER;
        } else {
          remainingQuantity = Math.max(
            0,
            (discount.quantityRemaining ?? discount.quantity) -
              usedInOtherItems -
              usedInCurrentItem,
          );
        }

        if (remainingQuantity <= 0) {
          // No remaining quantity, try next discount
          continue;
        }

        // Apply this discount to as many uncovered units as possible
        const applicableQuantity = Math.min(
          unitsStillUncovered,
          remainingQuantity,
        );

        if (applicableQuantity > 0) {
          const discountAmount = calculateDiscountAmount(
            discount,
            chargeItem.price,
            applicableQuantity,
          );
          // An extra charge at or above the price saves nothing, so the
          // member keeps the unit or credits.
          if (discount.additionalCharge && discountAmount <= 0) {
            continue;
          }

          const discountUse: DiscountUse = {
            discountId: discount.id,
            amount: discountAmount,
            quantity: applicableQuantity,
            order: recalculatedDiscounts.length,
            discount,
          };

          recalculatedDiscounts.push(discountUse);
          processedDiscountIds.add(discount.id);
          unitsStillUncovered -= applicableQuantity;

          // If all units are now covered, we're done
          if (unitsStillUncovered <= 0) {
            break;
          }
        }
      }
    }
  }

  // Preserve user order except for the global-percentage business rule: GLOBAL
  // percentage discounts must not run before item-level discounts, otherwise the
  // global sale percentage is calculated from the original item price instead
  // of the item-discounted balance. Fixed GLOBAL discounts keep their manually
  // selected position because fixed + percentage order can change the result.
  const maxItemDiscountOrder = recalculatedDiscounts.reduce(
    (maxOrder, discountUse) =>
      isGlobalDiscountUse(discountUse)
        ? maxOrder
        : Math.max(maxOrder, discountUse.order ?? 0),
    -1,
  );
  const adjustedOrder = (discountUse: DiscountUse): number => {
    const order = discountUse.order ?? 0;
    if (
      isGlobalPercentageDiscountUse(discountUse) &&
      order <= maxItemDiscountOrder
    ) {
      return maxItemDiscountOrder + 0.5 + order / 1000;
    }
    return order;
  };
  const orderedDiscounts = [...recalculatedDiscounts].sort(
    (a, b) => adjustedOrder(a) - adjustedOrder(b),
  );

  // Normalize order, quantity, and recalculate sequential amounts before returning
  return normalizeDiscounts(
    orderedDiscounts,
    chargeItem.quantity,
    chargeItem.price,
  );
}

/**
 * Calculate the discount amount for a charge item by recalculating applicable discounts
 * This is a convenience function that combines recalculateDiscountsForItem and getTotalDiscountAmount
 * Use this when you need both the recalculated discounts and the total discount amount
 */
export function calculateItemDiscountAmount(
  chargeItem: ChargeItem,
  patientDiscounts: DiscountDetail[],
  allChargeItems: ChargeItem[],
  dismissedDiscountIds?: ReadonlySet<string>,
): { discounts: DiscountUse[]; discountAmount: number } {
  const recalculatedDiscounts = recalculateDiscountsForItem(
    chargeItem,
    patientDiscounts,
    allChargeItems,
    dismissedDiscountIds,
  );
  const discountAmount = getTotalDiscountAmount(recalculatedDiscounts);
  return { discounts: recalculatedDiscounts, discountAmount };
}

/** Drop auto-applied patient discounts (those with a discountId); keep custom
 *  and global discount uses, which are managed/distributed separately. */
function stripAutoDiscounts(item: ChargeItem): ChargeItem {
  if (!item.discounts?.length) return item;
  return {
    ...item,
    discounts: item.discounts.filter((d) => !d.discountId),
  };
}

/**
 * Recalculate auto-applied patient discounts across ALL charge items as one
 * consistent allocation. Items are processed in order, and each item's result
 * is threaded forward so a shared discount's quantity is counted against the
 * items that already claimed it — a single discount can never be redeemed
 * beyond its available quantity, regardless of how many items it applies to.
 *
 * This is the single source of truth for discount application on a charge:
 * call it after any item mutation (add / remove / quantity change) and when the
 * available discounts load. It is idempotent — running it on an
 * already-correct charge returns the same allocation.
 *
 * The previous approach recalculated every item against the same pre-mutation
 * snapshot, so each item saw its siblings as having claimed nothing and the
 * same credit was handed out multiple times (over-redemption).
 */
export function buildDiscountRelevantChargeItemsSignature(
  chargeItems: ChargeItem[],
): string {
  return chargeItems
    .map((item) => {
      const lineSource = item.itemId ?? item.packageId ?? item.name ?? "";
      const discountSource = item.soldPackageId ?? item.bankedItemId ?? "";
      return [lineSource, discountSource, item.quantity, item.price].join("#");
    })
    .join("|");
}

export function recalculateAllDiscounts(
  chargeItems: ChargeItem[],
  availableDiscounts: DiscountDetail[],
  dismissedDiscountIds?: ReadonlySet<string>,
): ChargeItem[] {
  const finalized: ChargeItem[] = [];
  chargeItems.forEach((item, index) => {
    // Cross-item accounting context: items already finalized carry their real
    // allocations; items not yet processed are stripped so their stale (or
    // pre-mutation) allocations don't inflate the "used elsewhere" count.
    const laterItems = chargeItems.slice(index + 1).map(stripAutoDiscounts);
    const context = [...finalized, item, ...laterItems];
    const { discounts } = calculateItemDiscountAmount(
      item,
      availableDiscounts,
      context,
      dismissedDiscountIds,
    );
    finalized.push({ ...item, discounts });
  });
  return finalized;
}

/**
 * Sort comparator that orders discounts for maximum savings.
 * Percentage discounts come first (highest % first), then fixed-amount (highest first).
 * GLOBAL percentages are sorted into a final bucket so they cannot sort before
 * item-level discounts through non-transitive pairwise comparisons. When a
 * discount has both amount and percentage, we treat it as amount (prefer amount).
 */
export function optimalDiscountOrder(a: DiscountUse, b: DiscountUse): number {
  const aBucket = isGlobalPercentageDiscountUse(a) ? 1 : 0;
  const bBucket = isGlobalPercentageDiscountUse(b) ? 1 : 0;

  if (aBucket !== bBucket) return aBucket - bBucket;

  const aAmount = a.discount?.amount ?? a.createDiscount?.amount ?? 0;
  const bAmount = b.discount?.amount ?? b.createDiscount?.amount ?? 0;
  const aPercentage =
    a.discount?.percentage ?? a.createDiscount?.percentage ?? 0;
  const bPercentage =
    b.discount?.percentage ?? b.createDiscount?.percentage ?? 0;
  // When both amount and percentage exist, prefer amount (treat as amount discount)
  const aIsPercentage = aPercentage > 0 && !(aAmount > 0);
  const bIsPercentage = bPercentage > 0 && !(bAmount > 0);

  // Percentage discounts come first
  if (aIsPercentage && !bIsPercentage) return -1;
  if (!aIsPercentage && bIsPercentage) return 1;

  // Among percentage discounts, highest percentage first
  if (aIsPercentage && bIsPercentage) {
    return bPercentage - aPercentage;
  }

  // Among fixed amount discounts, highest amount first
  return bAmount - aAmount;
}

/**
 * Get a human-readable label for a discount (e.g. "10% off", "$5.00 off per item")
 */
export function getDiscountLabel(d: DiscountUse): string {
  const percentage = d.discount?.percentage ?? d.createDiscount?.percentage;
  const amount = d.discount?.amount ?? d.createDiscount?.amount;

  // Prefer amount for display when both exist - amount is more accurate
  if (amount && amount > 0) {
    return `${formatCurrency(amount)} off per item`;
  }
  if (percentage && percentage > 0) {
    return `${percentage}% off`;
  }
  return formatCurrency(d.amount) + " off";
}

/**
 * Get a human-readable type label for a discount (e.g. "Custom", "Membership")
 */
export function getDiscountTypeLabel(d: DiscountUse): string {
  if (d.discount?.type) {
    return formatToHumanReadable(String(d.discount.type));
  }
  if (d.createDiscount?.type) {
    return formatToHumanReadable(String(d.createDiscount.type));
  }
  return "Custom";
}

/**
 * Resolve the discount type used for source labeling. Banked / "saved for later"
 * discounts are marked in some legacy checkout paths only by
 * `createdByChargeItemId` (without the SAVED_ITEM type), so mirror
 * `available-discounts`' getDiscountSource and treat that as a saved item too.
 */
export function getEffectiveDiscountType(
  d: DiscountUse,
): DiscountType | undefined {
  if (d.discount?.createdByChargeItemId) {
    return DiscountType.SAVED_ITEM;
  }
  return d.discount?.type ?? d.createDiscount?.type;
}

/**
 * Get a human-readable source label for a discount that distinguishes where it
 * came from — a pre-purchased package, a membership, or a manual/custom discount
 * applied directly to the charge. Prefers the concrete entity name (package or
 * membership name, or the configured custom reason) and falls back to a generic label.
 *
 * Mirrors the backend `DiscountTimeline.source_name` logic so the checkout
 * preview and the charge timeline label discounts consistently.
 */
export function getDiscountSourceLabel(d: DiscountUse): string {
  const type = getEffectiveDiscountType(d);
  const comment = d.discount?.comment ?? d.createDiscount?.comment;
  const reason = d.discount?.reason ?? d.createDiscount?.reason;

  if (type === DiscountType.PACKAGE || d.discount?.soldPackageId) {
    return d.discount?.soldPackage?.package?.name || "Package discount";
  }
  if (type === DiscountType.MEMBERSHIP || d.discount?.memberId) {
    return d.discount?.member?.membership?.name || "Membership discount";
  }
  if (type === DiscountType.PROMOTION) {
    // The promotion name is not plumbed to the read model on this surface, so
    // fall back to the comment or a generic label.
    return comment || "Promotion";
  }
  if (type === DiscountType.SAVED_ITEM) {
    return "Saved for later";
  }
  if (type === DiscountType.CUSTOM || type === DiscountType.GLOBAL) {
    const label = reason ?? comment;
    return label ? `Custom (${label})` : "Custom discount";
  }
  // Source not hydrated (e.g. a persisted charge from getCharge, whose items
  // omit the nested discount) — degrade to a neutral label rather than falsely
  // claiming the discount was applied manually.
  return comment ? `Custom (${comment})` : "Discount";
}

/** Receipt fallback label for a membership-derived price reduction. */
export const MEMBERSHIP_BENEFIT_LABEL = "Membership benefit";
/** Receipt fallback label for a plain, manually-applied line discount. */
export const ITEM_DISCOUNT_LABEL = "Item discount";

/** The subset of a discount a receipt needs to label its source. */
export type ReceiptDiscountInfo = Pick<
  DiscountDetail,
  | "type"
  | "memberId"
  | "soldPackageId"
  | "createdByChargeItemId"
  | "reason"
  | "comment"
  | "member"
  | "soldPackage"
>;

/**
 * Patient-facing label for a discount on a receipt, naming where it came from
 * (package, membership, promotion, prepaid credit) instead of lumping every
 * reduction under "Item discount". Prefers the concrete entity name when the
 * source is hydrated, and degrades to a friendly type-based label otherwise.
 * Mirrors the backend `receipt_discount_label`.
 */
export function getReceiptDiscountLabel(
  discount: ReceiptDiscountInfo | undefined | null,
): string {
  if (!discount) return ITEM_DISCOUNT_LABEL;
  // "Saved for later" discounts are marked only by createdByChargeItemId in
  // some legacy paths, so resolve that to SAVED_ITEM first.
  const type = discount.createdByChargeItemId
    ? DiscountType.SAVED_ITEM
    : discount.type;

  if (type === DiscountType.PACKAGE || discount.soldPackageId) {
    return discount.soldPackage?.package?.name || "Package discount";
  }
  if (type === DiscountType.MEMBERSHIP || discount.memberId) {
    return discount.member?.membership?.name || MEMBERSHIP_BENEFIT_LABEL;
  }
  if (type === DiscountType.PROMOTION) {
    // The promotion name isn't plumbed to the receipt read model, so fall back
    // to the comment or a generic label.
    return discount.comment || "Promotion";
  }
  if (type === DiscountType.SAVED_ITEM) {
    return "Prepaid";
  }
  if (type === DiscountType.CASH_ROUNDING) {
    return "Cash rounded to nearest $0.05";
  }
  // CUSTOM / GLOBAL / unknown — a manually applied line discount.
  return discount.comment
    ? `${ITEM_DISCOUNT_LABEL} (${discount.comment})`
    : ITEM_DISCOUNT_LABEL;
}

export interface ReceiptDiscountLine {
  /** Source-aware label, e.g. a membership/package name or "Item discount". */
  label: string;
  /** Summed discount for this source on the line item, in cents. */
  amount: number;
}

/**
 * Group a line item's discount uses by source so a receipt renders one clearly
 * labeled row per discount (membership, package, promotion, item discount, …)
 * instead of a single opaque "Item discount" total. Accepts any discount-use
 * shape exposing `amount` and the nested `discount` (both `DiscountUse` and the
 * slimmer `DiscountUseDetail` used by the patient receipt qualify).
 */
export function getReceiptDiscountLines(
  discounts:
    | Array<{ amount: number; discount?: ReceiptDiscountInfo | null }>
    | undefined,
): ReceiptDiscountLine[] {
  if (!discounts?.length) return [];
  const byLabel = new Map<string, number>();
  for (const d of discounts) {
    const label = getReceiptDiscountLabel(d.discount);
    byLabel.set(label, (byLabel.get(label) ?? 0) + d.amount);
  }
  // Only cash rounding goes negative: rounding up adds the cents back.
  return Array.from(byLabel, ([label, amount]) => ({ label, amount })).filter(
    (line) => line.amount !== 0,
  );
}

/** A receipt discount line's amount with its sign: "-$5.00", or "+$0.02". */
export function formatReceiptDiscountAmount(amount: number): string {
  return `${amount > 0 ? "-" : "+"}${formatCurrency(Math.abs(amount))}`;
}

/**
 * Whether a discount was applied by a person (manual) or derived automatically
 * by the system from a pre-existing arrangement (package, membership, etc.).
 *
 * GLOBAL and CUSTOM are both applied on the spot by staff — they differ only in
 * scope (whole-charge vs single line item), so both count as manual.
 */
export type DiscountKind = "manual" | "automatic";

export function getDiscountKind(type: DiscountType | undefined): DiscountKind {
  switch (type) {
    case DiscountType.CUSTOM:
    case DiscountType.GLOBAL:
      return "manual";
    case DiscountType.PACKAGE:
    case DiscountType.MEMBERSHIP:
    case DiscountType.PROMOTION:
    case DiscountType.SAVED_ITEM:
      return "automatic";
    default:
      // Unknown source — treat as manual so it still stands out for review.
      return "manual";
  }
}

/**
 * Map the timeline `source` string (as emitted by the backend charge timeline)
 * to a DiscountType, so timeline rows can reuse the same labeling/badge logic
 * as the checkout page.
 */
export function discountSourceToType(
  source: string | null | undefined,
): DiscountType | undefined {
  switch (source) {
    case "package":
      return DiscountType.PACKAGE;
    case "membership":
      return DiscountType.MEMBERSHIP;
    case "promotion":
      return DiscountType.PROMOTION;
    case "saved_item":
      return DiscountType.SAVED_ITEM;
    case "global":
      return DiscountType.GLOBAL;
    case "custom":
      return DiscountType.CUSTOM;
    case "cash_rounding":
      return DiscountType.CASH_ROUNDING;
    default:
      return undefined;
  }
}

/**
 * Build a DiscountUse from custom discount form values.
 * Shared logic between cart and checkout custom discount creation.
 */
export function buildCustomDiscountUse(opts: {
  patientId: string;
  item?: Item;
  unitPrice: number;
  quantity: number;
  amountDollars: number;
  percentage: number;
  scope: "per-item" | "total";
  reason?: string;
  discountReasonId?: string | null;
  comment?: string;
}): DiscountUse {
  const {
    patientId,
    item,
    unitPrice,
    quantity,
    amountDollars,
    percentage,
    scope,
    discountReasonId,
    reason,
    comment,
  } = opts;
  const amountInCents = Math.round(amountDollars * 100);

  // Prefer amount over percentage when both exist - amount is more accurate
  const useAmount = amountInCents > 0;

  let finalDiscountAmount: number;
  if (useAmount) {
    finalDiscountAmount =
      scope === "per-item" ? amountInCents * quantity : amountInCents;
  } else if (percentage > 0) {
    finalDiscountAmount = Math.round((unitPrice * quantity * percentage) / 100);
  } else {
    finalDiscountAmount = 0;
  }

  const amountForBackend =
    scope === "per-item" ? amountInCents : Math.round(amountInCents / quantity);

  let discountUse: DiscountUse;

  if (item) {
    discountUse = createCustomDiscountUse(
      patientId,
      item,
      unitPrice,
      quantity,
      useAmount ? amountForBackend : undefined,
      useAmount ? undefined : percentage,
      reason,
      comment,
      discountReasonId,
    );
  } else {
    const createDiscountData: CreateCustomDiscountRequest = {
      patientId,
      type: DiscountType.CUSTOM,
      amount: useAmount ? amountForBackend : undefined,
      percentage: useAmount ? undefined : percentage,
      quantity: 1,
      discountReasonId,
      reason: reason || undefined,
      comment: comment || undefined,
    };

    discountUse = createDiscountUseFromCustom(
      createDiscountData,
      unitPrice,
      quantity,
    );
  }

  discountUse.amount = finalDiscountAmount;
  return discountUse;
}
